import {Component, OnInit, ViewEncapsulation } from '@angular/core';
import { AbstractControl, FormGroup, FormBuilder } from '@angular/forms';
import { EntitlementsHelperService } from '@ptl/account-management';
import { Brand, brandValues } from '@ptl/brand';
import { ContentStorageService, GlobalShadowStyleDirective, PtlWindowLocationService } from '@ptl/global-components';
import { Subscription } from 'rxjs';
import { AuthoringModeService } from 'src/app/shared/services/authoring-mode.service';

interface FormControls {
  region: AbstractControl;
}

interface FormControlsAsJson {
  region: string;
}

interface ContentConfig {
  region: string;
  url: string;
}

@Component({
    selector: 'app-state-based-find-a-doctor',
    templateUrl: './state-based-find-a-doctor.component.html',
    styleUrls: ['./state-based-find-a-doctor.component.scss'],
    encapsulation: ViewEncapsulation.ShadowDom,
    standalone: false
})
export class StateBasedFindADoctorComponent extends GlobalShadowStyleDirective implements OnInit {
  isAuthoring = false;
  form: FormGroup;
  formChangesSubscription: Subscription;
  doctorContent: Map<string, string>;
  pharmacyContent: Map<string, string>;
  contentStorageSubscription: Subscription;
  regionSelected: boolean;
  regions: { label: string; value: string; }[] = [];
  filteredRegions: { label: string; value: string; }[] = [];
  
  // Track content loading status
  doctorContentLoaded = false;
  pharmacyContentLoaded = false;

  constructor(
    private fb: FormBuilder,
    private entitlementsHelperService: EntitlementsHelperService,
    private windowLocationSvc: PtlWindowLocationService,
    private contentStorage: ContentStorageService,
    private authoringModeSvc: AuthoringModeService
  ) { 
    super();
  }

  ngOnInit(): void {
    this.contentStorageSubscription = this.contentStorage.contentChanged$.subscribe(async () => {
      // Try to get doctor content if not already loaded
      if (!this.doctorContentLoaded) {
        this.doctorContent = this.contentStorage.getContentMap('state-based-find-a-doctor');
        this.doctorContentLoaded = !!this.doctorContent;
      }
      
      // Try to get pharmacy content if not already loaded
      if (!this.pharmacyContentLoaded) {
        this.pharmacyContent = this.contentStorage.getContentMap('state-based-find-a-pharmacy');
        this.pharmacyContentLoaded = !!this.pharmacyContent;
      }
      
      // Only proceed when both content maps are available
      if (this.doctorContentLoaded && this.pharmacyContentLoaded) {
        await this.setupForms();
        this.setupSubscription();
      }
    });
    
    this.isAuthoring = this.authoringModeSvc.getAuthoringMode();
  }

  ngOnDestroy(): void {
    if (this.contentStorageSubscription) {
      this.contentStorageSubscription.unsubscribe();
    }
    if (this.formChangesSubscription) {
      this.formChangesSubscription.unsubscribe();
    }
  }

  trackByIndex(index: number, region: { label: string; value: string }): number {
    return index;
  }

  get formControls(): FormControls {
    return this.form.controls as unknown as FormControls;
  }

  get formValuesAsJson(): FormControlsAsJson {
    return this.form.value as unknown as FormControlsAsJson;
  }

  async setupForms() {
    const selectedAgency = await this.entitlementsHelperService.getSelectedAgencyPromise();
    const brandObjectsForAgencyRegion = brandValues.filter(b => b.region === selectedAgency.region);
    
    if (brandObjectsForAgencyRegion.length > 0) {
      const brandObj = brandObjectsForAgencyRegion[0];
      let brandsInSameStateSelectedAgency: Brand[] = brandValues.filter(b => b.state === brandObj.state);
      
      // Filter out generic PA region, but maintain single DE/WV regions
      if (brandsInSameStateSelectedAgency.length > 1) {
        brandsInSameStateSelectedAgency = brandsInSameStateSelectedAgency.filter(b => b.state !== b.region);
      }
      
      this.regions = brandsInSameStateSelectedAgency.map(b => {
        return {
          label: b.regionDescription,
          value: b.region
        }
      });
      
      this.form = this.fb.group({
        region: this.fb.control('')
      });
    }
  }

  setupSubscription() {
    this.formChangesSubscription = this.form.valueChanges
      .subscribe((v: FormControlsAsJson) => {
        this.regionSelected = !!v.region;
      });
  }

  navigateToFindADoctor() {
    const url = this.doctorContent.get(this.formValuesAsJson.region);
    if (url) {
      this.windowLocationSvc.goToUrl(url, { newTab: true });
    }
  }
  
  navigateToFindPharmacy() {
    const url = this.pharmacyContent.get(this.formValuesAsJson.region);
    if (url) {
      this.windowLocationSvc.goToUrl(url, { newTab: true });
    }
  }
}